import { UIEvent, useCallback, useEffect, useMemo } from 'react'

export type UseEntityInfiniteScrollParams = {
  containerId: string
  itemsLength: number
  skip?: number
  fetching: boolean
  loaded: boolean
  hasMore: boolean
  loadPage: (nextSkip: number) => void
}

export const useEntityInfiniteScroll = ({
  containerId,
  itemsLength,
  skip,
  fetching,
  loaded,
  hasMore,
  loadPage,
}: UseEntityInfiniteScrollParams) => {
  const nextSkip = useMemo(
    () => (typeof skip === 'number' && skip > 0 ? skip : itemsLength),
    [itemsLength, skip]
  )

  const loadMore = useCallback(() => {
    if (fetching || !loaded || !hasMore) {
      return
    }

    loadPage(nextSkip)
  }, [fetching, hasMore, loadPage, loaded, nextSkip])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (fetching || !loaded || !hasMore) {
        return
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMore()
      }
    },
    [fetching, hasMore, loadMore, loaded]
  )

  useEffect(() => {
    if (!fetching && hasMore) {
      const container = document.getElementById(containerId)

      if (container && container.scrollHeight <= container.clientHeight) {
        loadMore()
      }
    }
  }, [containerId, fetching, hasMore, loadMore, itemsLength])

  return {
    loadMore,
    handleScroll,
  }
}
